import { useState } from 'react'
import { Users, Plus, Trash2, ChevronDown, ChevronUp, Info } from 'lucide-react'
import type { Collaborateur, StatutCollab, SeuilIndividuel } from '../types'
import { heuresProductivesCollab, calcRevente } from '../calculs'
import { COLLAB_COLORS } from '../defaults'

interface Props {
  collaborateurs: Collaborateur[]
  onChange: (c: Collaborateur[]) => void
  seuilsIndividuels?: SeuilIndividuel[]
}

const e0 = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n)
const e2 = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)
const pct = (n: number) => `${Math.round(n)}%`

function CollabCard({
  c, onUpdate, onDelete, seuilIndiv
}: {
  c: Collaborateur
  onUpdate: (x: Collaborateur) => void
  onDelete: () => void
  seuilIndiv?: SeuilIndividuel
}) {
  const [open, setOpen] = useState(true)
  const [showReventeDetail, setShowReventeDetail] = useState(false)
  const set = <K extends keyof Collaborateur>(key: K, v: Collaborateur[K]) => onUpdate({ ...c, [key]: v })
  const hProd = heuresProductivesCollab(c)

  // Calculs revente — basés sur le coût d'achat HT mensuel
  const rv = calcRevente(c)
  const hasRevente = (c.venteMoisTTC ?? 0) > 0

  return (
    <div className="rounded-xl border border-gray-100 overflow-hidden">
      {/* ── En-tête de la card ── */}
      <div className="flex items-center justify-between px-4 py-2.5 cursor-pointer"
        style={{ background: `${c.couleur}10`, borderBottom: `2px solid ${c.couleur}` }}
        onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: c.couleur }} />
          <input type="text" value={c.prenom}
            onChange={e => { e.stopPropagation(); set('prenom', e.target.value) }}
            onClick={e => e.stopPropagation()}
            className="font-semibold text-sm bg-transparent border-none focus:outline-none focus:underline w-28"
            style={{ color: c.couleur }} />
          <input type="text" value={c.nom}
            onChange={e => { e.stopPropagation(); set('nom', e.target.value) }}
            onClick={e => e.stopPropagation()}
            className="text-sm bg-transparent border-none focus:outline-none focus:underline w-28 text-gray-500" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{e0(c.coutTotal)}/mois</span>
          <button onClick={e => { e.stopPropagation(); onDelete() }} className="text-gray-300 hover:text-red-400 transition-colors p-1">
            <Trash2 size={14} />
          </button>
          {open ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </div>
      </div>

      {open && (
        <div className="p-4 space-y-3 bg-white">

          {/* Statut + couleur */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Statut</label>
              <select value={c.statut} onChange={e => set('statut', e.target.value as StatutCollab)}
                className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300 bg-white">
                {(['Collaborateur', 'Manager', 'Apprenti'] as StatutCollab[]).map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Couleur</label>
              <div className="flex gap-1 flex-wrap">
                {COLLAB_COLORS.map(col => (
                  <button key={col} onClick={() => set('couleur', col)}
                    className="w-5 h-5 rounded-full border-2 transition-all"
                    style={{ background: col, borderColor: c.couleur === col ? '#111' : 'transparent' }} />
                ))}
              </div>
            </div>
          </div>

          {/* Coût & heures */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Coût total/mois (€)</label>
              <input type="number" min={0} value={c.coutTotal}
                onChange={e => set('coutTotal', +e.target.value)}
                className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Heures/mois</label>
              <input type="number" min={0} value={c.heuresMois}
                onChange={e => set('heuresMois', +e.target.value)}
                className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Efficacité (%)</label>
              <input type="number" min={0} max={100} value={c.tauxEfficacite}
                onChange={e => set('tauxEfficacite', +e.target.value)}
                className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300" />
            </div>
          </div>

          {/* Heures productives */}
          <div className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-1.5">
            → <span className="font-semibold text-gray-600">{hProd.toFixed(1)}h productives/mois</span>
            <span className="ml-1">({c.heuresMois}h × {c.tauxEfficacite}%)</span>
          </div>

          {/* CA réel */}
          <div>
            <label className="text-xs text-gray-500 mb-1 block">CA réel réalisé ce mois (€ TTC)</label>
            <input type="number" min={0} value={c.caReel ?? 0}
              onChange={e => set('caReel', +e.target.value)}
              className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300" />
          </div>

          {/* ═══════════════════════════════════════════════════════
              SEUIL INDIVIDUEL
          ════════════════════════════════════════════════════════ */}
          {seuilIndiv && (
            <div className="rounded-xl overflow-hidden" style={{ border: '2px solid #1A3C6E' }}>
              <div className="px-3 py-2 text-xs font-semibold text-white flex items-center gap-2" style={{ background: '#1A3C6E' }}>
                🎯 Seuil de rentabilité individuel
                <span className="ml-auto font-normal opacity-70">
                  Poids salarial : {pct(seuilIndiv.ratioPct)}
                </span>
              </div>

              {/* Décomposition du seuil */}
              <div className="divide-y divide-gray-50 text-xs bg-white">
                <div className="flex justify-between px-3 py-1.5">
                  <span className="text-gray-500">Coût direct (salaire + charges prime)</span>
                  <span className="font-mono text-gray-700">{e0(seuilIndiv.coutDirect)}</span>
                </div>
                <div className="flex justify-between px-3 py-1.5 bg-blue-50/40">
                  <span className="text-gray-500 flex items-center gap-1">
                    Quote-part charges fixes
                    <span className="text-blue-400" title={`${pct(seuilIndiv.ratioPct)} des charges fixes du salon`}>
                      <Info size={11} />
                    </span>
                  </span>
                  <span className="font-mono" style={{ color: '#1A3C6E' }}>{e0(seuilIndiv.quotePartCharges)}</span>
                </div>
                {seuilIndiv.quotePartBenef > 0 && (
                  <div className="flex justify-between px-3 py-1.5">
                    <span className="text-gray-500">Quote-part objectif bénéfice</span>
                    <span className="font-mono text-gray-700">{e0(seuilIndiv.quotePartBenef)}</span>
                  </div>
                )}
                <div className="flex justify-between px-3 py-1.5 font-semibold bg-blue-50/60">
                  <span className="text-gray-600">= Base HT à couvrir</span>
                  <span className="font-mono" style={{ color: '#1A3C6E' }}>{e0(seuilIndiv.seuilHT)}</span>
                </div>
              </div>

              {/* Résumé TTC + coût-minute */}
              <div className="grid grid-cols-2 divide-x divide-gray-100 border-t-2 border-blue-900/20">
                <div className="p-3 text-center">
                  <div className="text-xs text-gray-500 mb-1">Seuil TTC / mois</div>
                  <div className="text-xl font-black font-mono" style={{ color: '#1A3C6E' }}>
                    {e0(seuilIndiv.seuilTTC)}
                  </div>
                  <div className="text-xs text-gray-400">CA à générer</div>
                </div>
                <div className="p-3 text-center">
                  <div className="text-xs text-gray-500 mb-1">Coût-minute TTC</div>
                  <div className="text-xl font-black font-mono" style={{ color: '#1A3C6E' }}>
                    {e2(seuilIndiv.coutMinuteIndivTTC)}
                  </div>
                  <div className="text-xs text-gray-400">par minute productive</div>
                </div>
              </div>

              {/* Barre de couverture (si CA réel renseigné) */}
              {(c.caReel ?? 0) > 0 && seuilIndiv.seuilTTC > 0 && (() => {
                const coverage = Math.round((c.caReel! / seuilIndiv.seuilTTC) * 100)
                const color = coverage >= 100 ? '#16A34A' : coverage >= 80 ? '#E65100' : '#C62828'
                return (
                  <div className="px-3 pb-3 pt-1">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-500">Couverture (CA réel / seuil indiv.)</span>
                      <span className="font-bold" style={{ color }}>{coverage}%</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-gray-200">
                      <div className="h-2 rounded-full transition-all" style={{ width: `${Math.min(coverage, 100)}%`, background: color }} />
                    </div>
                    {coverage >= 100 && (
                      <div className="text-xs mt-1" style={{ color: '#16A34A' }}>✅ Seuil dépassé — prime déclenchable</div>
                    )}
                  </div>
                )
              })()}
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════
              REVENTE PRODUITS
          ════════════════════════════════════════════════════════ */}
          <div className="border-t border-gray-100 pt-3 space-y-2">
            <div className="text-xs font-semibold text-gray-600 flex items-center gap-2">
              🛍️ Revente produits
              {hasRevente && (
                <label className="flex items-center gap-1 ml-auto cursor-pointer font-normal text-gray-400">
                  <input type="checkbox" checked={showReventeDetail} onChange={e => setShowReventeDetail(e.target.checked)}
                    className="accent-green-600 cursor-pointer" />
                  Détail marge
                </label>
              )}
            </div>

            {/* Champs de saisie */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Ventes du mois TTC (€)</label>
                <input type="number" min={0} value={c.venteMoisTTC ?? 0}
                  onChange={e => set('venteMoisTTC', +e.target.value)}
                  placeholder="0"
                  className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-green-300" />
                <p className="text-xs text-gray-400 mt-0.5">Montant TTC vendu dans le mois</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Coefficient fournisseur</label>
                <input type="number" min={1} max={10} step={0.1} value={c.coeffRevente ?? 2.2}
                  onChange={e => set('coeffRevente', +e.target.value)}
                  className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-green-300" />
                <p className="text-xs text-gray-400 mt-0.5">Ex : 2.2 → achat = vente HT ÷ 2.2</p>
              </div>
              {/* ── Prime toggle HT / TTC ── */}
              <div className="col-span-2 rounded-xl border border-orange-100 p-3 space-y-2 bg-orange-50/40">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="text-xs font-medium text-gray-600">💰 Prime collaborateur</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Calculée sur :</span>
                    <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
                      <button type="button" onClick={() => set('primeBaseTTC', false)}
                        className="px-2.5 py-1 font-medium transition-colors"
                        style={{ background: !c.primeBaseTTC ? '#1A3C6E' : 'white', color: !c.primeBaseTTC ? 'white' : '#6B7280' }}>
                        HT
                      </button>
                      <button type="button" onClick={() => set('primeBaseTTC', true)}
                        className="px-2.5 py-1 font-medium transition-colors"
                        style={{ background: c.primeBaseTTC ? '#1A3C6E' : 'white', color: c.primeBaseTTC ? 'white' : '#6B7280' }}>
                        TTC
                      </button>
                    </div>
                    <span className="text-xs text-gray-400 italic">{c.primeBaseTTC ? 'prix client' : 'hors TVA'}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">% de prime</label>
                    <div className="relative">
                      <input type="number" min={0} max={100} value={c.primePct ?? 10}
                        onChange={e => set('primePct', +e.target.value)}
                        className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 pr-6 focus:outline-none focus:ring-1 focus:ring-blue-300 bg-white" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">Sur base {c.primeBaseTTC ? 'TTC' : 'HT'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Charges patronales (%)</label>
                    <div className="relative">
                      <input type="number" min={0} max={100} value={c.tauxChargesPrime ?? 45}
                        onChange={e => set('tauxChargesPrime', +e.target.value)}
                        className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 pr-6 focus:outline-none focus:ring-1 focus:ring-blue-300 bg-white" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">Charges employeur sur prime</p>
                  </div>
                </div>
                {hasRevente && (
                  <div className="grid grid-cols-3 divide-x divide-orange-100 rounded-lg overflow-hidden border border-orange-100 text-xs text-center">
                    <div className="px-2 py-1.5"><div className="text-gray-500">Base {c.primeBaseTTC?'TTC':'HT'}</div><div className="font-bold text-gray-700">{e0(rv.basePrime)}</div></div>
                    <div className="px-2 py-1.5 bg-orange-50"><div className="text-gray-500">💰 Prime à reverser</div><div className="font-bold" style={{color:'#E65100'}}>{e0(rv.primeBrute)}</div></div>
                    <div className="px-2 py-1.5"><div className="text-gray-500">Coût total salon</div><div className="font-bold" style={{color:'#C62828'}}>{e0(rv.coutPrimeSalon)}</div></div>
                  </div>
                )}
              </div>
            </div>

            {/* Détail du calcul de marge (case à cocher) */}
            {hasRevente && showReventeDetail && (
              <div className="rounded-xl overflow-hidden border border-gray-100">
                <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
                  Calcul de marge — {c.prenom}
                </div>
                <div className="divide-y divide-gray-50 text-xs">

                  <div className="px-3 py-1.5 bg-gray-50/50">
                    <div className="text-gray-400 text-xs font-medium mb-1">FLUX (calcul à partir des ventes TTC)</div>
                  </div>
                  <div className="flex justify-between px-3 py-1.5 bg-green-50">
                    <span className="text-gray-600 font-semibold">CA vente TTC encaissé</span>
                    <span className="font-bold text-green-700">{e0(rv.venteTTC)}</span>
                  </div>
                  <div className="flex justify-between px-3 py-1.5">
                    <span className="text-gray-600">÷ TVA (20%) = CA vente HT</span>
                    <span className="font-mono text-gray-700">{e0(rv.venteHT)}</span>
                  </div>
                  <div className="flex justify-between px-3 py-1.5">
                    <span className="text-gray-600">÷ Coeff {c.coeffRevente ?? 2.2} = Coût d'achat HT</span>
                    <span className="font-mono" style={{color:'#C62828'}}>− {e0(rv.achatHT)}</span>
                  </div>
                  <div className="flex justify-between px-3 py-1.5 bg-blue-50/40 font-semibold">
                    <span className="text-gray-700">= Marge brute HT</span>
                    <span className="font-mono" style={{ color: '#2563EB' }}>{e0(rv.margeBrute)}</span>
                  </div>

                  {/* Prime */}
                  <div className="px-3 py-1.5 bg-gray-50/50 mt-1">
                    <div className="text-gray-400 text-xs font-medium mb-1">PRIME COLLABORATEUR</div>
                  </div>
                  <div className="flex justify-between px-3 py-1.5 bg-orange-50">
                    <span className="text-gray-600">
                      💰 Prime à reverser ({c.primePct ?? 10}% × CA vente HT)
                    </span>
                    <span className="font-bold" style={{ color: '#E65100' }}>{e0(rv.primeBrute)}</span>
                  </div>
                  <div className="flex justify-between px-3 py-1.5">
                    <span className="text-gray-600">+ Charges patronales ({c.tauxChargesPrime ?? 45}%)</span>
                    <span className="font-mono" style={{ color: '#E65100' }}>+ {e0(rv.chargesPrime)}</span>
                  </div>
                  <div className="flex justify-between px-3 py-1.5">
                    <span className="text-gray-600">= Coût total prime pour le salon</span>
                    <span className="font-mono" style={{ color: '#C62828' }}>− {e0(rv.coutPrimeSalon)}</span>
                  </div>

                  {/* Bénéfice net */}
                  <div className="flex justify-between px-3 py-3 font-semibold"
                    style={{
                      background: rv.beneficeNet >= 0 ? '#F0FDF4' : '#FFF5F5',
                      borderTop: '2px solid ' + (rv.beneficeNet >= 0 ? '#86EFAC' : '#FCA5A5')
                    }}>
                    <div>
                      <div style={{ color: rv.beneficeNet >= 0 ? '#16A34A' : '#C62828' }}>= Bénéfice net pour le salon</div>
                      <div className="text-gray-400 font-normal">Marge − coût d'achat − prime + charges</div>
                    </div>
                    <span className="font-mono font-black text-sm" style={{ color: rv.beneficeNet >= 0 ? '#16A34A' : '#C62828' }}>
                      {rv.beneficeNet >= 0 ? '+' : '−'}{e0(Math.abs(rv.beneficeNet))}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  )
}

export default function SectionCollaborateurs({ collaborateurs, onChange, seuilsIndividuels }: Props) {
  const addCollab = () => {
    const nextColor = COLLAB_COLORS[collaborateurs.length % COLLAB_COLORS.length]
    const newC: Collaborateur = {
      id: Date.now().toString(),
      prenom: 'Nouveau',
      nom: '',
      statut: 'Collaborateur',
      coutTotal: 2000,
      heuresMois: 151,
      tauxEfficacite: 80,
      caReel: 0,
      couleur: nextColor,
      coutAchatMensuelHT: 0,
      venteMoisTTC: 0,
      coeffRevente: 2.2,
      primePct: 10,
      tauxChargesPrime: 45,
    }
    onChange([...collaborateurs, newC])
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-blue-600" />
          <span className="font-semibold text-sm text-gray-700">Collaborateurs</span>
          <span className="text-xs bg-blue-50 text-blue-600 rounded-full px-2 py-0.5">{collaborateurs.length}</span>
        </div>
        <button onClick={addCollab}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg text-white transition-colors"
          style={{ background: '#1A3C6E' }}>
          <Plus size={12} /> Ajouter
        </button>
      </div>
      <div className="p-4 space-y-3">
        {collaborateurs.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-4">Aucun collaborateur. Cliquez sur "Ajouter".</p>
        )}
        {collaborateurs.map((c) => {
          const seuilIndiv = seuilsIndividuels?.find(s => s.id === c.id)
          return (
            <CollabCard
              key={c.id}
              c={c}
              seuilIndiv={seuilIndiv}
              onUpdate={updated => onChange(collaborateurs.map(x => x.id === c.id ? updated : x))}
              onDelete={() => onChange(collaborateurs.filter(x => x.id !== c.id))}
            />
          )
        })}
      </div>
    </div>
  )
}
